import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_ALL_QUERY_RETURNS } from "../Queries/GetAllQueryReturns";
import { UPDATE_QUERY_RETURN } from "../Mutations/UpdateQueryReturn";
import { UserContext } from "../Contexts/UserContext";
import {
  Table,
  TableContainer,
  Paper,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Box,
  TablePagination,
  Modal,
  TextField,
  Button,
} from "@material-ui/core";
import { checkPermission } from "../utils/Roles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useStyles } from "../Styles/styles";
import { INCIDENT_AUTO_SUGGEST } from "../Queries/IncidentAutoSuggest";
import { withStyles } from "@material-ui/core/styles";
import { SET_QUERY_RETURNS } from "../Mutations/IncidentMutations/UpdateIncident";
import HelpIconButton from "./HelpIconButton";
import { ResizableBox } from "react-resizable";
import useWindowSize from "../utils/window-size";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { parseQuerySubscriptionMessage, useLocalStorage } from "../utils/utils";
import Highlighted from "./Highlighted";
import { returnStyle } from "../Styles/queryHighlighting";
import _ from "lodash";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { IncidentsContext } from "../Contexts/IncidentsContext";

import moment from "moment";

const StyledTableHeader = withStyles(() => ({
  root: {
    background: "#7a8697",
    height: "fit-content",
    padding: 0,
    textAlign: "center",
    color: "white",
    fontSize: "1rem",
    borderRight: "2px solid #e3e2e2",
  },
}))(TableCell);

const IncidentModal = ({
  modalOpen,
  setModalOpen,
  qr,
  updateQueryReturn,
  user,
  incidentMap,
  setIncidentMap,
}) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const [incident, setIncident] = useState(null);

  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_QUERY_RETURNS } = INCIDENT_ACTIONS;

  const [incidentAutoSuggest, { data: incidentAutoSuggestData }] = useLazyQuery(
    INCIDENT_AUTO_SUGGEST,
    {
      context: { serializationKey: "incidentQueue" },
      variables: { originatorId: user?.id },
      onError: error => {
        console.error(JSON.stringify(error));
      },
    },
  );

  useEffect(() => {
    if (searchValue && searchValue.length > 2) {
      incidentAutoSuggest({
        variables: { incidentNumber: searchValue, count: 20 },
      });
    }
  }, [searchValue, incidentAutoSuggest]);

  const handleClose = () => {
    setIncident(null);
    setSearchValue("");
    setModalOpen(false);
  };

  const handleKeyBlur = event => {
    if (event.keyCode === 27 || event.keyCode === 13) {
      handleClose();
    } else {
      return null;
    }
  };

  const [setQueryReturns] = useMutation(SET_QUERY_RETURNS, {
    onCompleted: data => {
      if (data) {
        if (data.setQueryReturns.errors.length > 0) {
          alert(
            "Error adding query return to incident: " +
              data.setQueryReturns.errors.join(", "),
          );
        }
        if (
          data.setQueryReturns.queryReturns.length > 0 &&
          data.setQueryReturns.incidentUuid
        ) {
          incidentDispatch({
            type: UPDATE_QUERY_RETURNS,
            payload: {
              incidentId: data.setQueryReturns.incidentUuid,
              queryReturns: data.setQueryReturns.queryReturns,
            },
          });
        }
      }
    },
  });

  const handleAddIncident = e => {
    e.preventDefault();
    if (incident?.id) {
      updateQueryReturn({
        variables: {
          input: {
            originatorId: user?.id,
            queryReturnId: qr?.id,
            incidentId: incident?.id,
          },
        },
        context: { serializationKey: "incidentQueue" },
      });
      setQueryReturns({
        variables: {
          input: {
            incidentId: incident?.id,
            officerId: user?.id,
            queryReturn: qr?.returnText + "\n",
            clear: false,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      setIncidentMap({
        ...incidentMap,
        [qr?.id]: incident,
      });
    }
    handleClose();
  };

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={true}
    >
      <div style={{ width: "auto" }} className={classes.paper}>
        <Autocomplete
          id="link-incident-autocomplete"
          autoSelect
          onKeyDown={handleKeyBlur}
          autoHighlight
          options={incidentAutoSuggestData?.incidentAutoSuggest || []}
          getOptionLabel={option => option?.incidentNumber}
          style={{ width: 300 }}
          onInputChange={(event, newValue) => setSearchValue(newValue)}
          onChange={(event, newValue) => {
            setIncident(newValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Enter an incident number"
              variant="outlined"
            />
          )}
        />
        <Box display="flex" flexDirection="row">
          <Button
            style={{
              width: "fit-content",
              marginTop: "1vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            variant="contained"
            onClick={e => handleAddIncident(e)}
            disabled={!incident}
          >
            Add
          </Button>
          <Button
            style={{
              width: "fit-content",
              marginTop: "1vh",
              marginBottom: "3vh",
            }}
            variant="contained"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </div>
    </Modal>
  );
};
const LinkButton = ({ handleIncidentOpening, incident, user }) => {
  const { locked, sealed } = incident || {};
  const canViewLocked = checkPermission(
    user?.userRoles,
    "view_locked_incident",
    "ALL",
    "",
    incident?.id,
  );
  const canViewSealed = checkPermission(
    user?.userRoles,
    "view_sealed_incident",
    "ALL",
    "",
    incident?.id,
  );
  const disabled =
    (!canViewLocked && locked) || (!canViewSealed && sealed) ? true : false;
  return (
    <button
      style={
        disabled
          ? {
              backgroundColor: "transparent",
              color: "#7d7d7d",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "none",
              display: "inline",
              margin: "0",
              padding: "0",
              "&:hover": {
                textDecoration: "none",
              },
              "&:focus": {
                textDecoration: "none",
              },
            }
          : {
              backgroundColor: "transparent",
              color: "#6096d1",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
              display: "inline",
              margin: "0",
              padding: "0",
              "&:hover": {
                textDecoration: "none",
              },
              "&:focus": {
                textDecoration: "none",
              },
            }
      }
      data-cy={"tablecell-incident-number-link-" + incident?.id}
      onClick={
        disabled
          ? () => alert("You do not have permission to view this incident")
          : () => {
              handleIncidentOpening(incident?.id, true);
            }
      }
    >
      {incident?.incidentNumber}
    </button>
  );
};

const QueryReturnTabWrapper = ({
  subscriptionServer,
  handleIncidentOpening,
  dashboardNavBarRef,
  tabsRef,
}) => {
  return (
    <>
      <QueryReturnTab
        handleIncidentOpening={handleIncidentOpening}
        subscriptionServer={subscriptionServer}
        dashboardNavBarRef={dashboardNavBarRef}
        tabsRef={tabsRef}
      />
    </>
  );
};

const QueryReturnTab = ({
  handleIncidentOpening,
  subscriptionServer,
  dashboardNavBarRef,
  tabsRef,
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [readMap, setReadMap] = useState({});
  const [incidentMap, setIncidentMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [qrToAdd, setQrToAdd] = useState(null);
  const [selectedQr, _setSelectedQr] = useState(null);
  const { user, responseErrorHandler, nightMode, onlineState } =
    useContext(UserContext);
  const [focused, setFocused] = useState(false);
  const myIndexRef = useRef(selectedQr);
  const windowSize = useWindowSize();

  const setSelectedQr = data => {
    myIndexRef.current = data;
    _setSelectedQr(data);
  };

  const returnsLength = useRef();
  const [getQueryReturns, { loading, data, error }] = useLazyQuery(
    GET_ALL_QUERY_RETURNS,
    {
      context: { serializationKey: "incidentQueue" },
      fetchPolicy: "no-cache",
      variables: {
        originatorId: user?.id,
        officerId: user?.id,
        page: page,
        perPage: 10,
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          let readObj = {};
          let incidentObj = {};
          data?.queryReturns.forEach(qr => {
            readObj[qr.id] = qr.read;
            incidentObj[qr.id] = qr?.incident;
          });
          setReadMap(readObj);
          setIncidentMap(incidentObj);
          returnsLength.current = data.queryReturns.length;
        }
      },
      onError: err => console.error(err),
    },
  );

  const [updateQueryReturn] = useMutation(UPDATE_QUERY_RETURN);

  const handleKeyDown = useCallback(
    event => {
      if (event.keyCode === 40 || event.keyCode === 38) {
        event.preventDefault();
      }
      const currentIndex = data?.queryReturns?.findIndex(
        qr => qr.id === selectedQr?.id,
      );
      if (event.keyCode === 40) {
        setSelectedQr(
          currentIndex + 1 < returnsLength.current
            ? data?.queryReturns?.[currentIndex + 1]
            : data?.queryReturns?.[currentIndex],
        );
      }
      if (event.keyCode === 38) {
        setSelectedQr(
          currentIndex - 1 >= 0
            ? data?.queryReturns?.[currentIndex - 1]
            : data?.queryReturns?.[currentIndex],
        );
      }
    },
    [data?.queryReturns, selectedQr?.id],
  );

  useEffect(() => {
    getQueryReturns();
  }, [getQueryReturns, page]);

  useEffect(() => {
    if (!selectedQr && data?.queryReturns?.length > 0) {
      setSelectedQr(data?.queryReturns[0]);
    }
  }, [data?.queryReturns, selectedQr]);
  useEffect(() => {
    if (!modalOpen && focused) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, focused, handleKeyDown]);

  const handleSubscription = useCallback(
    message => {
      const { type, officerId } = parseQuerySubscriptionMessage(message);
      if (
        parentRef.current &&
        type === "officerId" &&
        officerId?.toString() === user?.id?.toString()
      ) {
        console.log("Fetching Query Returns based on a sub server emit");
        getQueryReturns();
      } else {
        console.log("Not fetching Query Returns. ");
        console.log("parentRef.current: ", parentRef.current);
        console.log("OfficerId in message: ", officerId?.toString());
        console.log("userId: ", user?.id?.toString());
      }
    },
    [getQueryReturns, user?.id],
  );

  useEffect(() => {
    if (onlineState && user?.id) {
      subscriptionServer.subscribeToQueryReturnsForUser(user?.id, m =>
        handleSubscription(m),
      );
    }
  }, [handleSubscription, onlineState, subscriptionServer, user?.id]);

  const handleSelection = (id, bool, index) => {
    if (!id && index >= 0 && data) {
      id = data?.queryReturns[index]?.id;
    }
    if (readMap[id] !== bool && !!id) {
      updateQueryReturn({
        variables: {
          input: { originatorId: user?.id, queryReturnId: id, read: bool },
        },
        context: { serializationKey: "incidentQueue" },
      });
    }
    !!id &&
      setReadMap({
        ...readMap,
        [id]: true,
      });
    return index;
  };
  useEffect(() => {
    if (selectedQr?.id) {
      handleSelection(selectedQr?.id, true, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQr]);

  useEffect(() => {
    data &&
      document.getElementById("radio-log-table-div")?.focus({
        preventScroll: true,
      });
  }, [data]);

  const spanRef = useRef(null);
  const [tableHeight, setTableHeight] = useLocalStorage(
    `${user?.id}-query-return-table-height`,
    windowSize.height * 0.45,
  );
  const qrHeight =
    windowSize.height -
    dashboardNavBarRef?.current?.clientHeight -
    tabsRef?.current?.clientHeight -
    spanRef?.current?.clientHeight;

  const parentRef = useRef();
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (parentRef.current) {
      setWidth(parentRef.current.offsetWidth);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      parentRef.current = false;
    };
  }, []);

  if (loading && !data)
    return (
      <div style={{ color: nightMode ? "white" : "black" }}>Loading...</div>
    );
  if (error)
    return <div style={{ color: nightMode ? "white" : "black" }}>Error...</div>;
  return !data ? (
    <div>No data</div>
  ) : (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- This container receives focus for Up/Down navigation; tabbing will be reviewed separately.
    <div tabIndex={0}
      ref={parentRef}
      className={classes.root}
      id="radio-log-table-div"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <span ref={spanRef}>
        <ResizableBox
          width={width}
          height={tableHeight}
          handle={<DragHandleIcon className={`react-resizable-handle`} />}
          handleSize={[20, 20]}
          axis={"y"}
          onResizeStop={(event, { size }) => {
            setTableHeight(size.height);
          }}
        >
          <TableContainer className={classes.tableContainer} component={Paper}>
            <div
              className={
                nightMode
                  ? classes.tableH3ContainerDark
                  : classes.tableH3ContainerLight
              }
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                position="relative"
                width="100%"
              >
                <h3
                  data-cy={`h3-table-name-Query-Returns`}
                  className={
                    nightMode
                      ? classes.tableH3TitleDark
                      : classes.tableH3TitleLight
                  }
                >
                  Query Returns&nbsp;&nbsp;
                </h3>
                <HelpIconButton objectname="mobile_query_return" />
              </Box>
            </div>
            <Table
              size="small"
              data-cy="active-incident-table"
              stickyHeader
              aria-label="simple table"
            >
              <TableHead style={{ width: "1368px" }}>
                <TableRow style={{ width: "1368px" }}>
                  <StyledTableHeader>Date/Time</StyledTableHeader>
                  <StyledTableHeader>Incident #</StyledTableHeader>
                  <StyledTableHeader>Badge/ORI</StyledTableHeader>
                  <StyledTableHeader>Code</StyledTableHeader>
                  <StyledTableHeader>Source</StyledTableHeader>
                  <StyledTableHeader></StyledTableHeader>
                </TableRow>
              </TableHead>
              <TableBody style={{ width: "1368px" }}>
                {data?.queryReturns?.length < 1 || !data?.queryReturns ? (
                  <TableRow>
                    <TableCell>No data</TableCell>
                  </TableRow>
                ) : (
                  data.queryReturns.map((qr) => {
                    return (
                      <TableRow
                        hover
                        style={{
                          backgroundColor:
                            selectedQr?.id === qr?.id
                              ? nightMode
                                ? "darkblue"
                                : "lightblue"
                              : "inherit",
                        }}
                        onClick={() => {
                          setSelectedQr(qr);
                        }}
                        key={qr.id}
                      >
                        <TableCell
                          style={{
                            fontWeight: readMap[qr.id] ? undefined : "bold",
                          }}
                        >
                          {qr?.createdAt
                            ? moment
                                .utc(qr?.createdAt, "YYYY-MM-DD HH:mm:ss UTC")
                                .local()
                                .format("MM/DD/YYYY HH:mm")
                            : ""}
                        </TableCell>
                        <TableCell>
                          <LinkButton
                            handleIncidentOpening={handleIncidentOpening}
                            incident={incidentMap[qr.id]}
                            user={user}
                          />
                        </TableCell>
                        <TableCell>
                          {qr?.officer?.deptAttribute?.ori?.abbreviation}-
                          {qr?.officer?.deptAttribute?.badge}
                        </TableCell>
                        <TableCell>{qr?.mke}</TableCell>
                        <TableCell>{qr?.source}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => {
                              setModalOpen(true);
                              setQrToAdd(qr);
                            }}
                          >
                            Add
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ResizableBox>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10]}
          colSpan={6}
          count={-1}
          rowsPerPage={10}
          page={page}
          onPageChange={(e, newPage) => {
            if (newPage > 0) setPage(newPage);
          }}
          backIconButtonProps={page === 1 ? { disabled: true } : {}}
          labelDisplayedRows={() =>
            `${1 + 10 * (page - 1)} - ${10 * page}`
          }
        />
      </span>

      <QueryReturnHeader queryReturn={selectedQr} nightMode={nightMode} />
      <div
        style={{
          height: qrHeight || "auto",
          overflow: "auto",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div>
          {selectedQr?.returnObj && (
            <img
              alt="query-return"
              height="400"
              width="350"
              src={`data:image/jpeg;base64,${selectedQr?.returnObj}`}
            />
          )}
        </div>
        <Highlighted
          text={selectedQr?.returnText}
          highlightingArr={returnStyle(selectedQr?.mke)}
          nightMode={nightMode}
        />
      </div>
      <IncidentModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        qr={qrToAdd}
        updateQueryReturn={updateQueryReturn}
        user={user}
        onlineState={onlineState}
        incidentMap={incidentMap}
        setIncidentMap={setIncidentMap}
      />
    </div>
  );
};
const QueryReturnHeader = ({ queryReturn, nightMode }) => {
  const lookupError = {
    "Invalid User Password": "SWITCH PASSWORD IS INVALID",
    "Password has expired": "SWITCH PASSWORD HAS EXPIRED",
    "Invalid MFC Format": "CHECK FIELDS & TRY AGAIN",
    "Data Error for MFC name": "CHECK FIELDS & TRY AGAIN",
    "Disabled user ID": "USER ACCOUNT IS NOT ACTIVE, CONTACT DPS",
    "Invalid destination": "CHECK FIELDS & TRY AGAIN",
    "Field INVALID DATA": "CHECK FIELDS & TRY AGAIN",
    "Certification date has expired.": "NCIC CERTIFICATION HAS EXPIRED",
    "Change password rejected – Old password is incorrect.":
      "CHANGE PASSWORD REJECTED - OLD PASSWORD IS INCORRECT",
    "Change password rejected – New password was recently used.":
      "CHANGE PASSWORD REJECTED - PASSWORD WAS RECENTLY USED",
    "Change password rejected – Invalid user ID.":
      "CHANGE PASSWORD REJECTED - INVALID USER ID",
    "Change password rejected – New password must contain 3 of num.":
      "CHANGE PASSWORD REJECTED - DOES NOT MEET COMPLEXITY REQUIREMENTS",
    "Error during parsing XML.": "ERROR PARSING XML, CONTACT DPS",
  };
  const text = queryReturn?.returnText || "";
  const escapeRX = s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regexError = new RegExp(
    `(${Object.keys(lookupError).map(escapeRX).join("|")})`,
    "i",
  );

  const splitError = text.split(regexError);
  const mkeWarningRegex = /^MKE\/[^\r\n]*/gm;
  const armedAndDangerousRegex = /ARMED AND DANGEROUS/i;
  const suspendedLicenseRegex = /^FIELD VALUE: (Criminal|Civil)$/gim;
  const suspendedRegistrationRegex =
    /^REGISTRATION STATUS DESCRIPTION:\s*Suspended Registration$/gim;
  const expiredRegistrationRegex =
    /^REGISTRATION STATUS DESCRIPTION:\s*Expired Registration$/gim;

  let importantText = text.match(mkeWarningRegex) || [];

  if (armedAndDangerousRegex.test(text)) {
    importantText = [...importantText, "ARMED AND DANGEROUS"];
  }
  if (suspendedLicenseRegex.test(text)) {
    importantText = [...importantText, "POSSIBLE SUSPENDED LICENSE"];
  }
  if (suspendedRegistrationRegex.test(text)) {
    importantText = [...importantText, "POSSIBLE SUSPENDED REGISTRATION"];
  }
  if (expiredRegistrationRegex.test(text)) {
    importantText = [...importantText, "POSSIBLE EXPIRED REGISTRATION"];
  }

  importantText = _.uniq(importantText);
  const importantErrorText = _.uniq(
    splitError.filter(part => regexError.test(part)),
  );

  return (
    <div
      style={{
        backgroundColor: nightMode ? "#616161" : "#eee",
        borderTopLeftRadius: "5px",
        borderTopRightRadius: "5px",
        padding: "10px 12px",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <h3
        data-cy={`Query-Return-Header-${queryReturn?.id}`}
        style={{
          color: nightMode ? "white" : "#303e4f",
          margin: "0px",
          fontFamily: "sans-serif",
          fontWeight: "bold",
          fontSize: "1rem",
        }}
      >
        {queryReturn?.officer?.deptAttribute?.ori?.abbreviation}-
        {queryReturn?.officer?.deptAttribute?.badge}: {queryReturn?.mke}{" "}
        {queryReturn?.createdAt
          ? moment
              .utc(queryReturn?.createdAt, "YYYY-MM-DD HH:mm:ss UTC")
              .local()
              .format("MM/DD/YYYY HH:mm")
          : ""}
      </h3>
      {importantText.map((text, index) => {
        return (
          <h3
            key={"warning-text-" + index}
            style={{
              color: nightMode ? "white" : "red",
              margin: "0px",
              fontFamily: "sans-serif",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "3px solid red",
              backgroundColor: nightMode ? "red" : "inherit",
            }}
          >
            {text}
          </h3>
        );
      })}
      {importantErrorText.map((text, index) => {
        return (
          <h3
            key={index}
            style={{
              color: nightMode ? "red" : "red",
              margin: "0px",
              fontFamily: "sans-serif",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "3px solid red",
              backgroundColor: nightMode ? "yellow" : "yellow",
            }}
          >
            {lookupError[text]}
          </h3>
        );
      })}
      <div></div>
    </div>
  );
};

export default QueryReturnTabWrapper;
